import { useEffect, useState } from "react";
import useBodyScrollLock from "@/hooks/useBodyScrollLock";
import styles from "./CookieConsent.module.css";

const COOKIE_NAME = "cookie_preferences";

const setCookie = (name, value, days = 365) => {
  const expires = new Date(Date.now() + days * 86400000).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)}; expires=${expires}; path=/`;
};

const getCookie = (name) =>
  document.cookie
    .split("; ")
    .find((row) => row.startsWith(`${name}=`))
    ?.split("=")[1];

const necessary = (val) => ({
  _User_Country_Home_: `${val ? "granted" : "denied"}`,
  LS_CSRF_TOKEN: `${val ? "granted" : "denied"}`,
  utm_medium: `${val ? "granted" : "denied"}`,
  _gcl_au: `${val ? "granted" : "denied"}`,
  utm_source: `${val ? "granted" : "denied"}`,
  "_ga_*": `${val ? "granted" : "denied"}`,
  G_ENABLED_IDPS: `${val ? "granted" : "denied"}`,
  "cookieyes-consent": `${val ? "granted" : "denied"}`,
  "rc::a": `${val ? "granted" : "denied"}`,
  __stripe_mid: `${val ? "granted" : "denied"}`,
  "rc::c": `${val ? "granted" : "denied"}`,
});

const functional = (val) => ({
  "ytidb::LAST_RESULT_ENTRY_KEY": `${val ? "granted" : "denied"}`,
  _scid: `${val ? "granted" : "denied"}`,
});

const analytics = (val) => ({
  _ga: `${val ? "granted" : "denied"}`,
  _fbp: `${val ? "granted" : "denied"}`,
  _gid: `${val ? "granted" : "denied"}`,
  "_gat_UA-*": `${val ? "granted" : "denied"}`,
  utm_campaign: `${val ? "granted" : "denied"}`,
});

const performance = (val) => ({
  GCLB: `${val ? "granted" : "denied"}`,
  AWSALB: `${val ? "granted" : "denied"}`,
});

const advertisement = (val) => ({
  MUID: `${val ? "granted" : "denied"}`,
  YSC: `${val ? "granted" : "denied"}`,
  VISITOR_INFO1_LIVE: `${val ? "granted" : "denied"}`,
  VISITOR_PRIVACY_METADATA: `${val ? "granted" : "denied"}`,
  "__Host-GAPS": `${val ? "granted" : "denied"}`,
  sc_at: `${val ? "granted" : "denied"}`,
  _ttp: `${val ? "granted" : "denied"}`,
  _tt_enable_cookie: `${val ? "granted" : "denied"}`,
});

const toConsentString = (prefs) => {
  const consentId =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : String(Date.now());

  return [
    `consentid:${consentId}`,
    "consent:yes",
    "action:yes",
    `necessary:${prefs.necessary ? "yes" : "no"}`,
    `functional:${prefs.functional ? "yes" : "no"}`,
    `analytics:${prefs.analytics ? "yes" : "no"}`,
    `performance:${prefs.performance ? "yes" : "no"}`,
    `advertisement:${prefs.advertisement ? "yes" : "no"}`,
  ].join(",");
};

const pushConsentUpdate = (prefs) => {
  if (typeof window === "undefined") return;

  if (typeof window.gtag === "function") {
    window.gtag("consent", "update", {
      analytics_storage: prefs.analytics ? "granted" : "denied",
      ad_storage: prefs.advertisement ? "granted" : "denied",
      ad_user_data: prefs.advertisement ? "granted" : "denied",
      ad_personalization: prefs.advertisement ? "granted" : "denied",
    });
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: "consent_datalayer_update",
    ...necessary(prefs.necessary),
    ...functional(prefs.functional),
    ...analytics(prefs.analytics),
    ...performance(prefs.performance),
    ...advertisement(prefs.advertisement),
    ...prefs,
  });
};

const TAB_DESCRIPTIONS = {
  necessary:
    "Necessary cookies are required for the website to function, such as secure login, bookings, and saving your choices.",
  functional:
    "Functional cookies will help us remember your preferences and provide a more personalised browsing experience.",
  analytics:
    "Analytics cookies help us understand how visitors use our website so we can improve it.",
  performance:
    "These cookies support increasing website speed, stability, and overall performance.",
  advertisement:
    "Advertisement cookies are used to showcase the relevant travel deals and offers.",
};

const DEFAULT_PREFS = {
  necessary: true,
  functional: true,
  analytics: true,
  performance: true,
  advertisement: true,
  uncategorized: true,
};

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [prefs, setPrefs] = useState(DEFAULT_PREFS);

  useBodyScrollLock(showCustomize);

  useEffect(() => {
    if (getCookie(COOKIE_NAME)) return undefined;

    const timer = window.setTimeout(() => {
      setShowBanner(true);
    }, 5000);

    return () => window.clearTimeout(timer);
  }, []);

  const savePrefs = () => {
    pushConsentUpdate(prefs);
    setCookie(COOKIE_NAME, toConsentString(prefs));
    setShowBanner(false);
    setShowCustomize(false);
  };

  const acceptAll = () => {
    const all = { ...DEFAULT_PREFS };
    pushConsentUpdate(all);
    setCookie(COOKIE_NAME, toConsentString(all));
    setPrefs(all);
    setShowBanner(false);
    setShowCustomize(false);
  };

  const rejectAll = () => {
    const reject = {
      necessary: true,
      functional: false,
      analytics: false,
      performance: false,
      advertisement: false,
      uncategorized: false,
    };

    pushConsentUpdate(reject);
    setCookie(COOKIE_NAME, toConsentString(reject));
    setPrefs(reject);
    setShowBanner(false);
    setShowCustomize(false);
  };

  const toggle = (key) => {
    setPrefs((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  return (
    <>
      {showBanner ? (
        <div className={styles.banner}>
          <div className={styles.bannerInner}>
            <div className={styles.bannerContent}>
              <h3 className={`heading2 ${styles.cookiehead}`}>We Value Your Privacy</h3>
              <p className={styles.cookiedesc}>
                Click &quot;Accept All&quot; to agree to the cookie storing policy of Next Holidays
                for an enhanced experience and personalize content on the website.
              </p>
            </div>

            <div className={styles.btnbox}>
              <button
                type="button"
                className={`${styles.outlineBtn} ${styles.mobileOnly}`}
                onClick={() => setShowCustomize(true)}
              >
                Customize
              </button>

              <div className={styles.btnGroup}>
                <button
                  type="button"
                  className={`${styles.outlineBtn} ${styles.desktopOnly}`}
                  onClick={() => setShowCustomize(true)}
                >
                  Customize
                </button>
                <button type="button" className={styles.primaryBtn} onClick={acceptAll}>
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      {showCustomize ? (
        <div className={styles.overlay}>
          <div className={styles.modal}>
            <div className={styles.stickhead}>
              <div className={styles.modalHeader}>
                <h2 className={styles.constumModalhead}>Customize Consent Preferences</h2>
                <button
                  type="button"
                  className={styles.closeBtn}
                  onClick={() => setShowCustomize(false)}
                >
                  ✕
                </button>
              </div>
            </div>

            <div className={styles.modalContent}>
              <p className={styles.modalContent2}>
                While cookies are strictly necessary for the operation of this website, Next Holidays
                also uses cookies to remember your preferences, enhance your browsing experience, and
                offer personalised travel content.
                <br />
                <br />
                We also use these tools to understand how our website is used, improve performance,
                and show relevant deals and offers to you. We may use cookies for advertising or
                enabling content sharing on social platforms.
              </p>
            </div>

            {[
              ["necessary", "Necessary", "Always Active"],
              ["functional", "Functional"],
              ["analytics", "Analytics"],
              ["performance", "Performance"],
              ["advertisement", "Advertisement"],
            ].map(([key, label, extra]) => (
              <div key={key} className={styles.cookieSection}>
                <div className={styles.sectionHeader}>
                  <p className={styles.pointshead}>{label}</p>
                  {extra ? (
                    <span className={styles.alwaysActive}>{extra}</span>
                  ) : (
                    <label className={styles.switch} onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        checked={prefs[key]}
                        onChange={() => toggle(key)}
                      />
                      <span className={styles.slider} />
                    </label>
                  )}
                </div>
                <p className={styles.sectionDesc}>{TAB_DESCRIPTIONS[key]}</p>
              </div>
            ))}

            <div className={styles.stickfooter}>
              <div className={styles.modalFooter}>
                <button type="button" className={styles.outlineBtn} onClick={rejectAll}>
                  Reject All
                </button>
                <button type="button" className={styles.outlineBtn} onClick={savePrefs}>
                  Save My Preferences
                </button>
                <button type="button" className={styles.primaryBtn} onClick={acceptAll}>
                  Accept All
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </>
  );
}
